import to from 'to-case';

import type { TranslatorProps } from 'react-i18next';
import { getStore } from 'services/ServiceRegistry';
import UserStore from 'stores/UserStore';
import { Roles } from '../../../permissions/defs';

type EmailNotification = {
  mail_type_id: string,
  mail_key: string,
  subscribed: boolean,
  only_assigned_to_me: boolean,
  hidden?: boolean,
};

type AppNotification = {
  enable: number,
  event_type_id: string,
  id: string,
  name: string,
  threshold: Array,
  available_thresholds: Array,
  user_id: string,
  app_notifications_only?: boolean,
};

export type UserNotifications = {
  email: EmailNotification[],
  app: AppNotification[],
};

type MergedNotifications = [EmailNotification & AppNotification];

export const disabledNotificationTypes = ['ImportDsError', 'LargeAccountDoesNotHaveAssets'];

const mergeUserNotificationSettings = ({ app, email }): MergedNotifications => {
  const settings = [];
  const { activeUser }: UserStore = getStore(['userStore'])[0];
  const roleId = activeUser.role_id;
  const isUserRole = Roles.USER === roleId;

  for (const emailSetting of email) {
    const type = to.pascal(emailSetting.mail_key);
    const isAssetCalDue = emailSetting.mail_key === 'asset-calibration-due';
    const appSetting = app.filter(item => item.name === type)[0];

    emailSetting.hidden = isAssetCalDue && isUserRole;

    if (!disabledNotificationTypes.includes(type))
      settings.push({ ...emailSetting, ...appSetting });
  }

  for (const appSetting of app) {
    const type = appSetting.name;
    const settingsHasAppSettings = settings.filter(item => type === item.name);

    if (settingsHasAppSettings.length === 0) {
      appSetting.app_notifications_only = true;
      settings.push(appSetting);
    }
  }

  return settings;
};

const getNotificationThresholds = (setting, t: TranslatorProps): Array => {
  const thresholds = [];
  const staticThresholds = [7, 14, 28, 45, 60];
  const itemThreshold = setting.threshold;
  const type = setting.name || to.pascal(setting.mail_key);

  for (const threshold of staticThresholds) {
    thresholds.push({
      name: `threshold_${threshold}`,
      label: t(`general:notifications.${type}_help`, { threshold }) || '',
      value: Boolean(setting.enable && itemThreshold.includes(threshold)),
    });
  }

  return thresholds;
};

export const makeUserNotificationFields = (
  userNotifications: UserNotifications,
  t: TranslatorProps
): Array => {
  const settings = mergeUserNotificationSettings(userNotifications);
  const fields = [];

  for (const setting of settings) {
    const type = setting.name === undefined ? to.pascal(setting.mail_key) : setting.mail_key;

    const field = {
      name: setting.name || to.pascal(setting.mail_key),
      label: t(`general:notifications.${setting.name || to.pascal(setting.mail_key)}`),
      extra: {
        app_notifications_only: setting.app_notifications_only,
        hidden: setting.hidden,
      },
      fields: [
        {
          name: 'email',
          label: t(`general:notifications.${type}_help`),
          value: setting.only_assigned_to_me ? false : setting.subscribed,
        },
        {
          name: 'emailOnlyMe',
          label: t(`general:notifications.${type}_assigned`),
          value: setting.only_assigned_to_me,
        },
        {
          name: 'app',
          label: t('general:notifications.only_in_app'),
          value: !setting.subscribed && !setting.only_assigned_to_me && Boolean(setting.enable),
        },
        {
          name: 'thresholds',
          label: t(`general:notifications.${setting.name}_frequency`),
          fields: getNotificationThresholds(setting, t),
          extra: {
            helperText: setting.app_notifications_only
              ? t('general:notifications.threshold_text_app_only')
              : t('general:notifications.threshold_text_email'),
          },
        },
      ],
    };

    fields.push(field);
  }

  return fields.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
};
